import { FC } from 'react';
import { Typography } from '@mui/material';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import Heading from 'components/Heading';
import { Column, Row } from 'further-ui/components/Layout';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';
import { grey } from '@mui/material/colors';

type ImportErrors = {
  transactionTypeIdsInUse?: Array<string>;
  accountCodeNameConflicts?: Array<{
    accountCode: string;
    accountNames: Array<string>;
  }>;
  accountNameCodeConflicts?: Array<{
    accountName: string;
    accountCodes: Array<string>;
  }>;
};

type Props = {
  open: boolean;
  onClose: () => void;
  errors: ImportErrors | null;
};

const ImportErrorsDialog: FC<Props> = ({ open, onClose, errors }) => {
  if (!errors) return null;

  const {
    transactionTypeIdsInUse,
    accountCodeNameConflicts,
    accountNameCodeConflicts,
  } = errors;

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      title="Import Errors"
      maxWidth="md"
      actions={{
        confirm: { onClick: onClose, label: 'Close' },
        cancel: { hidden: true },
      }}
      content={
        <Column spacing="sm">
          {transactionTypeIdsInUse && transactionTypeIdsInUse.length > 0 && (
            <>
              <Heading variant="h5">Transaction Types In Use</Heading>
              <Typography variant="body2">
                The following transaction types cannot be deleted because they
                have transactions:
              </Typography>
              {transactionTypeIdsInUse.map((errorString) => {
                const parts = errorString.split(':');
                const id = parts[0];
                const name = parts[1] || 'Unknown';
                const accountCode = parts[2] || 'Unknown';

                return (
                  <Column key={errorString}>
                    <Row centered spacing="xxs">
                      <Typography variant="body2">
                        <b>
                          <ColorIndicatorWithText
                            size={6}
                            color={grey[900]}
                            text={name}
                          />
                        </b>
                      </Typography>
                      <Typography variant="body2">
                        (Account: {accountCode})
                      </Typography>
                    </Row>
                    <Row centered spacing="xxs">
                      <Typography variant="body2">
                        <ColorIndicatorWithText
                          size={6}
                          color={grey[900]}
                          text="ID"
                        />
                      </Typography>
                      <Typography variant="body2">{id}</Typography>
                    </Row>
                  </Column>
                );
              })}
            </>
          )}

          {accountCodeNameConflicts && accountCodeNameConflicts.length > 0 && (
            <>
              <Heading variant="h5">Account Code Conflicts</Heading>
              <Typography variant="body2">
                The same account code has been used with different account
                names:
              </Typography>
              {accountCodeNameConflicts.map((conflict) => (
                <Column key={conflict.accountCode}>
                  <Row spacing="xxs" centered>
                    <Typography variant="body2">
                      <ColorIndicatorWithText
                        size={6}
                        color={grey[900]}
                        text="Account code:"
                      />
                    </Typography>
                    <Typography variant="body2">
                      <b>{conflict.accountCode}</b>
                    </Typography>
                  </Row>

                  <Row spacing="xxs" centered>
                    <Typography variant="body2">
                      <ColorIndicatorWithText
                        size={6}
                        color={grey[900]}
                        text="Account names:"
                      />
                    </Typography>
                    <Typography variant="body2">
                      <b>{conflict.accountNames.join(', ')}</b>
                    </Typography>
                  </Row>
                </Column>
              ))}
            </>
          )}

          {accountNameCodeConflicts && accountNameCodeConflicts.length > 0 && (
            <>
              <Heading variant="h5">Account Name Conflicts</Heading>
              <Typography variant="body2">
                The same account name has been used with different account
                codes:
              </Typography>
              {accountNameCodeConflicts.map((conflict) => (
                <Column key={conflict.accountName}>
                  <Row spacing="xxs" centered>
                    <ColorIndicatorWithText
                      color={grey[900]}
                      size={6}
                      text="Account name:"
                    />
                    <Typography variant="body2">
                      <b>{conflict.accountName}</b>
                    </Typography>
                  </Row>
                  <Row spacing="xxs" centered>
                    <Typography variant="body2">
                      <ColorIndicatorWithText
                        color={grey[900]}
                        size={6}
                        text="Account codes:"
                      />
                    </Typography>
                    <Typography variant="body2">
                      <b>{conflict.accountCodes.join(', ')}</b>
                    </Typography>
                  </Row>
                </Column>
              ))}
            </>
          )}
        </Column>
      }
    />
  );
};

export default ImportErrorsDialog;
